import { t } from '@lingui/core/macro';
import { AddressLabel, type AddressFallbackRecord, type UserGroupAddressRecord } from '@navio/nsdk';
import { revalidateLogic } from '@tanstack/react-form';
import { invalidateQuery, noddiAsync, URLKeys } from 'noddi-async';
import { ApiErrorMessageInternal, FieldErrorMessage, NoddiButton, NoddiDialog, useAppForm } from 'noddi-ui-web';
import { CountryCode } from 'noddi-util';
import { AddressPicker } from 'noddi-web';
import { useState } from 'react';
import { z } from 'zod';
import { buildManualProfileAddressBody, isAddressFallbackComplete } from '../../utils/addressFallback';
import { getCommonTranslations } from '../../utils/commonTranslations';
import { addressLabels, getAddressLabel } from './addressLabels';

const addressSchema = z
  .object({
    label: z.enum(addressLabels),
    customLabel: z.string(),
    instructions: z.string()
  })
  .superRefine((value, context) => {
    if (value.label === 'custom' && !value.customLabel.trim()) {
      context.addIssue({ code: 'custom', message: t`Enter a label`, path: ['customLabel'] });
    }
  });

type FormValues = z.input<typeof addressSchema>;

const FORM_ID = 'customer-address-form';

type Props = {
  address?: UserGroupAddressRecord;
  open: boolean;
  onClose: () => void;
  userGroupId: number;
};

export const AddressDialog = ({ address, open, onClose, userGroupId }: Props) => {
  const addressLabelOptions = addressLabels.map((value) => ({ value, label: getAddressLabel(value) }));
  const [selectedAddress, setSelectedAddress] = useState<{ id: number; name: string }>();
  const [addressFallback, setAddressFallback] = useState<AddressFallbackRecord | null>(null);
  const [showAddressError, setShowAddressError] = useState(false);
  const [isResolvingAddress, setIsResolvingAddress] = useState(false);
  const isEditing = address !== undefined;

  const invalidateAddresses = async () => {
    await invalidateQuery({ urlKey: URLKeys.getUserGroupAddresses });
  };

  const {
    mutate: createAddress,
    isPending: isCreating,
    error: createError
  } = noddiAsync.usePost({
    type: URLKeys.postUserGroupAddress,
    queryConfig: {
      onSuccess: async () => {
        await invalidateAddresses();
        onClose();
      }
    }
  });
  const {
    mutate: updateAddress,
    isPending: isUpdating,
    error: updateError
  } = noddiAsync.usePatch({
    type: URLKeys.patchUserGroupAddress,
    queryConfig: {
      onSuccess: async () => {
        await invalidateAddresses();
        onClose();
      }
    }
  });

  const form = useAppForm({
    defaultValues: {
      label: address?.label ?? AddressLabel.HOME,
      customLabel: address?.customLabel ?? '',
      instructions: address?.instructions ?? ''
    } satisfies FormValues,
    validationLogic: revalidateLogic(),
    validators: { onSubmit: addressSchema, onDynamic: addressSchema },
    onSubmit: ({ value }) => {
      const customLabel = value.label === 'custom' ? value.customLabel.trim() : undefined;

      if (isEditing) {
        updateAddress({
          userGroupAddressId: address.id,
          label: value.label,
          customLabel,
          instructions: value.instructions.trim() || undefined
        });
        return;
      }

      if (!selectedAddress && !isAddressFallbackComplete(addressFallback)) {
        setShowAddressError(true);
        return;
      }

      createAddress({
        userGroupId,
        label: value.label,
        customLabel,
        instructions: value.instructions.trim() || undefined,
        ...(selectedAddress
          ? { addressId: selectedAddress.id }
          : { address: buildManualProfileAddressBody(addressFallback!, CountryCode.NORWAY) })
      });
    }
  });

  const isPending = isCreating || isUpdating || isResolvingAddress;
  const mutationError = createError ?? updateError;

  return (
    <form.AppForm>
      <NoddiDialog
        open={open}
        onClose={onClose}
        title={isEditing ? t`Edit address` : t`Add address`}
        footer={
          <div className='flex flex-col gap-4'>
            {mutationError && <ApiErrorMessageInternal error={mutationError} />}
            <div className='flex justify-end gap-2'>
              <NoddiButton type='button' variant='outline' disabled={isPending} onClick={onClose}>
                {t`Cancel`}
              </NoddiButton>
              <form.SubscribeButton
                formId={FORM_ID}
                disabled={isPending}
                label={isEditing ? t`Save address` : t`Add address`}
              />
            </div>
          </div>
        }
      >
        <form
          id={FORM_ID}
          className='flex flex-col gap-4'
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          {!isEditing && (
            <>
              <AddressPicker
                enableFallback
                skipIsInDeliveryAreaCheck
                address={selectedAddress}
                onAddressResolutionChange={setIsResolvingAddress}
                onAddressCreation={(nextAddress) => {
                  setSelectedAddress(nextAddress);
                  setShowAddressError(false);
                }}
                onFallbackChange={(fallback) => {
                  setAddressFallback(fallback);
                  setShowAddressError(false);
                }}
                translations={getCommonTranslations().addressSearch}
                fallbackTranslations={{
                  cantFindAddress: t`Can't find your address? Enter it manually`,
                  backToSearch: t`Back to address search`,
                  address: t`Address`,
                  addressPlaceholder: t`Address`,
                  streetNumber: t`Number`,
                  streetNumberPlaceholder: t`Nr.`,
                  zipcode: t`Zipcode`,
                  zipcodePlaceholder: t`Zipcode`,
                  city: t`City`,
                  cityPlaceholder: t`City`
                }}
              />
              {showAddressError && (
                <FieldErrorMessage
                  errorMessage={t`Select an address from the list or complete all manual address fields`}
                />
              )}
            </>
          )}

          <form.AppField name='label'>
            {(field) => <field.Select label={t`Label`} options={addressLabelOptions} />}
          </form.AppField>
          <form.Subscribe selector={(state) => state.values.label}>
            {(label) =>
              label === AddressLabel.CUSTOM && (
                <form.AppField name='customLabel'>{(field) => <field.Input label={t`Custom label`} />}</form.AppField>
              )
            }
          </form.Subscribe>
          <form.AppField name='instructions'>{(field) => <field.TextArea label={t`Instructions`} />}</form.AppField>
        </form>
      </NoddiDialog>
    </form.AppForm>
  );
};
